import React from 'react';
import InfoHint from './InfoHint';

/* ---------- Deep (max) property record renderer (#1059, #1093) ----------
 *
 * Shared renderer for the parcel's sourced public-records dossier. Post-#1093
 * it is SUBSTANCE-FIRST: it LEADS with a synthesized, information-dense
 * narrative organized into thematic sections (Ownership & Title, Mineral
 * Rights & Federal Land, Access & Water, ...). The raw datapoints become
 * collapsible CITED EVIDENCE beneath each section; all gaps collapse into one
 * small muted line. When a record has no narrative yet (the two pre-#1093 seed
 * records, or a still-running job), it gracefully falls back to the old
 * grouped-by-source datapoint view.
 *
 * `maxRecord` shape (from GET /parcels/{id}/max-record):
 *   null                       -> loading
 *   { has_max_record: false }  -> loaded-but-empty
 *   { has_max_record: true, n_datapoints, n_sources, n_gaps, target_met,
 *     generated_at|ingested_at,
 *     narrative: { summary, sections: [{ key, title, body, citations:[int] }],
 *                  gaps: [str],
 *                  checklist: [{ item, status:'covered'|'not_determined',
 *                                value, source, basis }] } | null,
 *     gaps: [str],
 *     datapoints: [{ path, value, source, scope, confidence,
 *                    corroborating_sources }],   // confirmed, citation-aligned
 *     sources: [{ source, count, datapoints: [...] }] }
 */
export const DEEP_RECORD_CONFIDENCE_STYLE = {
  high: { bg: '#e8f5e9', fg: '#2e7d32' },
  medium: { bg: '#fff8e1', fg: '#b78103' },
  low: { bg: '#ffe0e0', fg: '#b71c1c' },
};

export function DeepRecordConfidenceChip({ confidence }) {
  const key = (confidence || '').toLowerCase();
  const style = DEEP_RECORD_CONFIDENCE_STYLE[key] || { bg: '#f0f0f0', fg: '#777' };
  return (
    <span
      style={{
        minWidth: 44,
        padding: '1px 8px',
        borderRadius: 10,
        background: style.bg,
        color: style.fg,
        fontSize: '0.68rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      {confidence || 'n/a'}
    </span>
  );
}

// Minimal, dependency-free markdown for the narrative body: **bold** inline,
// "- "/"* " bullet lists, and blank-line-separated paragraphs. The synthesis
// prompt keeps bodies tight (fact-per-clause), so this covers what we emit.
function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function NarrativeBody({ body }) {
  const lines = String(body || '').split('\n');
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} style={{ margin: '4px 0 4px 18px', padding: 0 }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ marginBottom: 2 }}>{renderInline(b)}</li>
          ))}
        </ul>,
      );
      bullets = [];
    }
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) { flushBullets(); return; }
    if (/^[-*]\s+/.test(line)) {
      bullets.push(line.replace(/^[-*]\s+/, ''));
    } else {
      flushBullets();
      blocks.push(
        <p key={`p-${blocks.length}`} style={{ margin: '3px 0' }}>{renderInline(line)}</p>,
      );
    }
  });
  flushBullets();
  return <div style={{ fontSize: '0.86rem', color: '#333', lineHeight: 1.5 }}>{blocks}</div>;
}

function EvidenceList({ points }) {
  if (!points || points.length === 0) return null;
  return (
    <details data-testid="pd-deep-record-evidence" style={{ marginTop: 4 }}>
      <summary
        style={{
          cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
          color: '#6b7a90', padding: '2px 0', userSelect: 'none',
        }}
      >
        Evidence · {points.length} source{points.length === 1 ? '' : 's'}
      </summary>
      <div style={{ marginTop: 4, paddingLeft: 6, borderLeft: '2px solid #e5e9f0' }}>
        {points.map((dp, di) => (
          <div
            key={`${dp.path}-${di}`}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '4px 0 4px 8px', fontSize: '0.8rem',
              borderBottom: '1px solid #f2f4f7',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '0.68rem', color: '#98a2b3', wordBreak: 'break-word' }}>
                {dp.path}
              </span>
              <span style={{ fontWeight: 500, color: '#222', wordBreak: 'break-word' }}>
                {dp.value == null || dp.value === '' ? '—' : dp.value}
              </span>
              {dp.source && (
                <span style={{ display: 'block', fontSize: '0.68rem', color: '#7a8aa0', wordBreak: 'break-word' }}>
                  {dp.source}
                  {dp.corroborating_sources && dp.corroborating_sources.length > 0
                    && ` (+${dp.corroborating_sources.length} corroborating)`}
                </span>
              )}
            </span>
            <DeepRecordConfidenceChip confidence={dp.confidence} />
          </div>
        ))}
      </div>
    </details>
  );
}

// Evidence-basis chip for a covered checklist item (mirrors refinement 2's
// grounding levels: confirmed / single-source / inferred).
const CHECKLIST_BASIS_STYLE = {
  confirmed: { bg: '#e8f5e9', fg: '#2e7d32' },
  'single-source': { bg: '#fff8e1', fg: '#b78103' },
  inferred: { bg: '#eef2ff', fg: '#3949ab' },
};

function ChecklistBasisChip({ basis }) {
  const key = (basis || '').toLowerCase();
  const style = CHECKLIST_BASIS_STYLE[key];
  if (!style) return null;
  return (
    <span
      style={{
        padding: '0 6px', borderRadius: 8, background: style.bg, color: style.fg,
        fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.3, whiteSpace: 'nowrap',
      }}
    >
      {basis}
    </span>
  );
}

// Required-bits checklist (#1110): a FIXED list of the items every report must
// account for. Covered items show a check + concise value + source; unresolved
// items show a muted "not determined" — never silently missing.
function ChecklistView({ checklist }) {
  if (!checklist || checklist.length === 0) return null;
  return (
    <div data-testid="pd-deep-record-checklist" style={{ marginBottom: 16 }}>
      <h4
        style={{
          fontSize: '0.84rem', fontWeight: 700, color: '#1565c0',
          margin: '0 0 6px', paddingBottom: 3, borderBottom: '1px solid #e5e9f0',
        }}
      >
        Required checks
      </h4>
      <div>
        {checklist.map((c, ci) => {
          const covered = c.status === 'covered';
          return (
            <div
              key={c.item || ci}
              data-testid="pd-deep-record-checklist-item"
              data-status={c.status}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '5px 0', fontSize: '0.82rem',
                borderBottom: '1px solid #f2f4f7',
              }}
            >
              <span
                aria-hidden
                style={{
                  flex: '0 0 auto', width: 16, textAlign: 'center', fontWeight: 700,
                  color: covered ? '#2e7d32' : '#b0b8c4',
                }}
              >
                {covered ? '✓' : '—'}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600, color: covered ? '#222' : '#8894a6' }}>
                  {c.item}
                </span>
                {covered ? (
                  <>
                    {c.value != null && c.value !== '' && (
                      <span style={{ display: 'block', color: '#333', wordBreak: 'break-word' }}>
                        {c.value}
                      </span>
                    )}
                    {c.source && (
                      <span style={{ display: 'block', fontSize: '0.68rem', color: '#7a8aa0', wordBreak: 'break-word' }}>
                        {c.source}
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: '0.72rem', fontStyle: 'italic', color: '#98a2b3' }}>
                    not determined
                  </span>
                )}
              </span>
              {covered && <ChecklistBasisChip basis={c.basis} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NarrativeView({ narrative, datapoints, gaps }) {
  const dps = datapoints || [];
  const sections = (narrative.sections || []).filter((s) => s && s.body);
  const gapList = (narrative.gaps && narrative.gaps.length ? narrative.gaps : gaps) || [];
  return (
    <div data-testid="pd-deep-record-narrative">
      <ChecklistView checklist={narrative.checklist} />
      {narrative.summary && (
        <p style={{ fontSize: '0.9rem', color: '#1a2b45', fontWeight: 600, margin: '2px 0 12px', lineHeight: 1.45 }}>
          {narrative.summary}
        </p>
      )}
      {sections.map((section, si) => {
        const cited = (section.citations || [])
          .map((idx) => dps[idx])
          .filter(Boolean);
        return (
          <div key={section.key || si} data-testid="pd-deep-record-section" style={{ marginBottom: 16 }}>
            <h4
              style={{
                fontSize: '0.84rem', fontWeight: 700, color: '#1565c0',
                margin: '0 0 4px', paddingBottom: 3, borderBottom: '1px solid #e5e9f0',
              }}
            >
              {section.title}
            </h4>
            <NarrativeBody body={section.body} />
            <EvidenceList points={cited} />
          </div>
        );
      })}
      {gapList.length > 0 && (
        <div
          data-testid="pd-deep-record-gaps"
          style={{
            marginTop: 10, padding: '8px 12px', borderRadius: 6,
            background: '#f7f8fa', color: '#8894a6', fontSize: '0.78rem', lineHeight: 1.5,
          }}
        >
          <span style={{ fontWeight: 600 }}>Not resolved from public records: </span>
          {gapList.join(', ')}.
        </div>
      )}
    </div>
  );
}

function GroupedView({ sources }) {
  return (
    <>
      {(sources || []).map((group, gi) => (
        <div key={`${group.source}-${gi}`} style={{ marginBottom: 14 }} data-testid="pd-deep-record-source-group">
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: '0.82rem', fontWeight: 700, color: '#334',
              padding: '4px 0', borderBottom: '1px solid #e5e9f0',
              marginBottom: 6,
            }}
          >
            <span style={{ flex: 1, wordBreak: 'break-word' }}>{group.source}</span>
            <span style={{ fontSize: '0.72rem', color: '#8894a6', fontWeight: 600 }}>
              {group.count}
            </span>
          </div>
          <div>
            {(group.datapoints || []).map((dp, di) => (
              <div
                key={`${dp.path}-${di}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '5px 0', fontSize: '0.82rem',
                  borderBottom: '1px solid #f2f4f7',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '0.7rem', color: '#98a2b3', wordBreak: 'break-word' }}>
                    {dp.path}
                  </span>
                  <span style={{ fontWeight: 500, color: '#222', wordBreak: 'break-word' }}>
                    {dp.value == null || dp.value === '' ? '—' : dp.value}
                  </span>
                  {dp.scope && (
                    <span style={{ marginLeft: 6, fontSize: '0.66rem', color: '#aab2c0' }}>
                      ({dp.scope})
                    </span>
                  )}
                </span>
                <DeepRecordConfidenceChip confidence={dp.confidence} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

// Rendered as a card by default (matches the ParcelDetail detail-card
// styling). Pass `bare` to skip the outer card/heading chrome when the
// caller supplies its own (e.g. the live research view, which owns the
// header + job status above the growing datapoint list).
export default function DeepRecordSection({ maxRecord, bare = false }) {
  // Loading (null) vs. loaded-but-empty (has_max_record === false).
  const loading = maxRecord == null;
  const has = !loading && maxRecord.has_max_record;
  const narrative = has && maxRecord.narrative && (maxRecord.narrative.sections || []).length > 0
    ? maxRecord.narrative
    : null;

  const generatedLabel = (() => {
    const ts = maxRecord && (maxRecord.generated_at || maxRecord.ingested_at);
    if (!ts) return null;
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
  })();

  const body = (
    <>
      {!bare && (
        <h3
          id="tab-deep-record"
          style={{ fontSize: '0.95rem', color: '#1565c0', fontWeight: 600, padding: '4px 0', margin: 0, display: 'flex', alignItems: 'center' }}
        >
          <span>Deep Record</span>
          <InfoHint label="About the Deep Record">
            A sourced public-records dossier assembled by the property-record
            maximizer: confirmed findings are synthesized into a substance-first
            narrative, with every fact backed by a cited, confidence-labeled
            source. Records are generated per-parcel from county assessor, GIS,
            recorder, BLM/GLO, treasurer and other public sources.
          </InfoHint>
        </h3>
      )}

      {loading && (
        <div style={{ padding: '10px 2px', fontSize: '0.85rem', color: '#999' }}>
          Loading deep record…
        </div>
      )}

      {!loading && !has && (
        <div
          data-testid="pd-deep-record-empty"
          style={{
            padding: '16px', borderRadius: 8, background: '#f7f8fa',
            border: '1px dashed #cfd6e0', color: '#555', fontSize: '0.85rem',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, color: '#333', marginBottom: 4 }}>
            No deep record yet
          </div>
          A sourced public-records dossier has not been generated for this
          parcel. When one is produced, its narrative and cited sources will
          appear here.
        </div>
      )}

      {!loading && has && (
        <div data-testid="pd-deep-record-body">
          {/* Summary pill */}
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 8, marginBottom: 12,
              background: '#eef4fb',
            }}
          >
            <span style={{ fontWeight: 700, color: '#1565c0', fontSize: '0.9rem' }}>
              {maxRecord.n_datapoints} findings
            </span>
            <span style={{ color: '#7a8aa0' }}>·</span>
            <span style={{ fontWeight: 600, color: '#333', fontSize: '0.85rem' }}>
              {maxRecord.n_sources} sources
            </span>
            {generatedLabel && (
              <>
                <span style={{ color: '#7a8aa0' }}>·</span>
                <span style={{ color: '#666', fontSize: '0.8rem' }}>
                  generated {generatedLabel}
                </span>
              </>
            )}
            {maxRecord.target_met != null && (
              <span
                style={{
                  marginLeft: 'auto', padding: '2px 10px', borderRadius: 10,
                  fontSize: '0.72rem', fontWeight: 700,
                  background: maxRecord.target_met ? '#e8f5e9' : '#fff8e1',
                  color: maxRecord.target_met ? '#2e7d32' : '#b78103',
                }}
              >
                {maxRecord.target_met ? 'Target met' : 'Below target'}
              </span>
            )}
          </div>

          {narrative ? (
            <NarrativeView
              narrative={narrative}
              datapoints={maxRecord.datapoints}
              gaps={maxRecord.gaps}
            />
          ) : (
            <GroupedView sources={maxRecord.sources} />
          )}
        </div>
      )}
    </>
  );

  if (bare) return <div data-testid="pd-deep-record-card">{body}</div>;
  return (
    <div className="detail-card" data-testid="pd-deep-record-card">
      {body}
    </div>
  );
}
